import React, { useCallback, useEffect, useState } from "react";
import {
    Alert,
    Badge,
    Button,
    Card,
    Col,
    Form,
    Modal,
    Row,
    Spinner,
    Table,
} from "react-bootstrap";
import AnnouncementAdminService from "../../services/announcement/AnnouncementAdminService";
import { FaPlus } from "react-icons/fa6";

const EMPTY_FORM = {
    title: "",
    summary: "",
    content: "",
    type: "UPDATE",
    imageUrl: "",
    startAt: "",
    endAt: "",
    targetType: "ALL",
    targetValue: "",
    priority: 10,
    active: true,
};

const toLocalDateTimeInput = (value) => {
    if (!value) return "";

    const date = new Date(value);
    const localDate = new Date(
        date.getTime() - date.getTimezoneOffset() * 60000
    );

    return localDate.toISOString().slice(0, 16);
};

const fromLocalDateTimeInput = (value) =>
    value ? new Date(value).toISOString() : null;

const formatDate = (value) =>
    value ? new Date(value).toLocaleString("id-ID") : "—";

const getErrorMessage = (error, fallback) => {
    const responseData = error?.response?.data;
    return typeof responseData === "string" ? responseData : fallback;
};

export default function AnnouncementAdmin() {
    const [items, setItems] = useState([]);
    const [form, setForm] = useState({ ...EMPTY_FORM });
    const [editingId, setEditingId] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const loadAnnouncements = useCallback(async () => {
        setLoading(true);
        setError("");

        try {
            const response = await AnnouncementAdminService.list();
            const data = response.data;

            setItems(
                Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : []
            );
        } catch (requestError) {
            setError(
                getErrorMessage(
                    requestError,
                    "Gagal memuat pengumuman. Periksa koneksi dan hak akses admin."
                )
            );
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadAnnouncements();
    }, [loadAnnouncements]);

    const openCreateModal = () => {
        setEditingId(null);
        setForm({ ...EMPTY_FORM });
        setError("");
        setShowModal(true);
    };

    const openEditModal = (announcement) => {
        setEditingId(announcement.id);
        setForm({
            title: announcement.title || "",
            summary: announcement.summary || "",
            content: announcement.content || "",
            type: announcement.type || "UPDATE",
            imageUrl: announcement.imageUrl || "",
            startAt: toLocalDateTimeInput(announcement.startAt),
            endAt: toLocalDateTimeInput(announcement.endAt),
            targetType: announcement.targetType || "ALL",
            targetValue: announcement.targetValue || "",
            priority: announcement.priority ?? 0,
            active: Boolean(announcement.active),
        });
        setError("");
        setShowModal(true);
    };

    const updateField = (field, value) => {
        setForm((currentForm) => ({
            ...currentForm,
            [field]: value,
        }));
    };

    const closeModal = () => {
        if (saving) return;
        setShowModal(false);
        setError("");
    };

    const saveAnnouncement = async (event) => {
        event.preventDefault();
        setSaving(true);
        setError("");
        setSuccess("");

        const payload = {
            ...form,
            startAt: fromLocalDateTimeInput(form.startAt),
            endAt: fromLocalDateTimeInput(form.endAt),
            priority: Number(form.priority),
        };

        try {
            if (editingId) {
                await AnnouncementAdminService.update(editingId, payload);
                setSuccess("Pengumuman berhasil diperbarui.");
            } else {
                await AnnouncementAdminService.create(payload);
                setSuccess("Pengumuman berhasil dibuat.");
            }

            setShowModal(false);
            await loadAnnouncements();
        } catch (requestError) {
            setError(
                getErrorMessage(
                    requestError,
                    "Gagal menyimpan pengumuman. Periksa data dan hak akses."
                )
            );
        } finally {
            setSaving(false);
        }
    };

    const deleteAnnouncement = async (announcement) => {
        const confirmed = window.confirm(
            `Hapus pengumuman "${announcement.title}"? Tindakan ini tidak dapat dibatalkan.`
        );

        if (!confirmed) return;

        setError("");
        setSuccess("");

        try {
            await AnnouncementAdminService.remove(announcement.id);
            setSuccess("Pengumuman berhasil dihapus.");
            await loadAnnouncements();
        } catch (requestError) {
            setError(
                getErrorMessage(requestError, "Gagal menghapus pengumuman.")
            );
        }
    };

    const renderTargetValuePlaceholder = () => {
        switch (form.targetType) {
            case "ROLE":
                return "Contoh: 1,2";
            case "UPT":
                return "Kode UPT";
            case "TRADER":
                return "Kode trader";
            default:
                return "Tidak diperlukan";
        }
    };

    return (
        <div className="container-fluid">
            <Card className="mb-4 bg-transparent border-0 shadow-none">
                <Card.Body className="px-1 py-0">
                    <Row className="align-items-end">
                        <Col md={7}>
                            <h3 className="text-white fw-bold mb-2">Manajemen Pengumuman</h3>
                            <div className="text-white">
                                Kelola informasi fitur baru, pembaruan aplikasi, maintenance, dan
                                informasi penting.
                            </div>
                        </Col>
                        <Col xs="auto" md="5" className="d-flex justify-content-md-end mt-3 mt-md-0">
                            <Button
                                variant="warning"
                                size="sm"
                                disabled={loading}
                                onClick={openCreateModal}
                            >
                                <FaPlus className="me-1" /> Buat Pengumuman
                            </Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {error && !showModal && (
                <Alert
                    variant="danger"
                    dismissible
                    onClose={() => setError("")}
                >
                    {String(error)}
                </Alert>
            )}

            {success && (
                <Alert
                    variant="success"
                    dismissible
                    onClose={() => setSuccess("")}
                >
                    {success}
                </Alert>
            )}

            <Card>
                <Card.Body>
                    {loading ? (
                        <div className="text-center p-4">
                            <Spinner animation="border" />
                        </div>
                    ) : (
                        <Table responsive hover>
                            <thead>
                                <tr>
                                    <th>Judul</th>
                                    <th>Tipe</th>
                                    <th>Target</th>
                                    <th>Periode</th>
                                    <th>Prioritas</th>
                                    <th>Status</th>
                                    <th>Aksi</th>
                                </tr>
                            </thead>

                            <tbody>
                                {items.length === 0 ? (
                                    <tr>
                                        <td
                                            colSpan={7}
                                            className="text-center text-muted py-4"
                                        >
                                            Belum ada pengumuman. Klik “Buat Pengumuman” untuk
                                            menambahkan.
                                        </td>
                                    </tr>
                                ) : (
                                    items.map((announcement) => (
                                        <tr key={announcement.id}>
                                            <td>
                                                <strong>{announcement.title}</strong>
                                                <div className="small text-muted">
                                                    {announcement.summary}
                                                </div>
                                            </td>

                                            <td>
                                                <Badge
                                                    bg={
                                                        announcement.type === "IMPORTANT"
                                                            ? "danger"
                                                            : announcement.type === "MAINTENANCE"
                                                                ? "warning"
                                                                : "primary"
                                                    }
                                                >
                                                    {announcement.type}
                                                </Badge>
                                            </td>

                                            <td>
                                                {announcement.targetType}
                                                {announcement.targetValue
                                                    ? `: ${announcement.targetValue}`
                                                    : ""}
                                            </td>

                                            <td className="small">
                                                {formatDate(announcement.startAt)}
                                                <br />
                                                s.d. {formatDate(announcement.endAt)}
                                            </td>

                                            <td>{announcement.priority}</td>

                                            <td>
                                                <Badge
                                                    bg={announcement.active ? "success" : "secondary"}
                                                >
                                                    {announcement.active ? "Aktif" : "Nonaktif"}
                                                </Badge>
                                            </td>

                                            <td>
                                                <Button
                                                    size="sm"
                                                    variant="outline-primary"
                                                    className="me-2"
                                                    onClick={() => openEditModal(announcement)}
                                                >
                                                    Edit
                                                </Button>

                                                <Button
                                                    size="sm"
                                                    variant="outline-danger"
                                                    onClick={() => deleteAnnouncement(announcement)}
                                                >
                                                    Hapus
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
            </Card>

            <Modal
                show={showModal}
                onHide={closeModal}
                size="lg"
                backdrop="static"
            >
                <Form onSubmit={saveAnnouncement}>
                    <Modal.Header closeButton={!saving}>
                        <Modal.Title>
                            {editingId ? "Edit Pengumuman" : "Buat Pengumuman"}
                        </Modal.Title>
                    </Modal.Header>

                    <Modal.Body>
                        {error && <Alert variant="danger">{String(error)}</Alert>}

                        <Row className="g-3">
                            <Col md={8}>
                                <Form.Group>
                                    <Form.Label>Judul *</Form.Label>
                                    <Form.Control
                                        required
                                        maxLength={200}
                                        value={form.title}
                                        onChange={(event) =>
                                            updateField("title", event.target.value)
                                        }
                                    />
                                </Form.Group>
                            </Col>

                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>Tipe *</Form.Label>
                                    <Form.Select
                                        value={form.type}
                                        onChange={(event) =>
                                            updateField("type", event.target.value)
                                        }
                                    >
                                        <option value="FEATURE">Fitur Baru</option>
                                        <option value="UPDATE">Pembaruan</option>
                                        <option value="IMPORTANT">Penting</option>
                                        <option value="MAINTENANCE">Maintenance</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label>Ringkasan</Form.Label>
                                    <Form.Control
                                        maxLength={500}
                                        value={form.summary}
                                        onChange={(event) =>
                                            updateField("summary", event.target.value)
                                        }
                                    />
                                </Form.Group>
                            </Col>

                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label>Isi Pengumuman *</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={5}
                                        required
                                        value={form.content}
                                        onChange={(event) =>
                                            updateField("content", event.target.value)
                                        }
                                    />
                                    <Form.Text className="text-muted">
                                        Gunakan teks biasa dan hindari memasukkan data rahasia atau
                                        kredensial.
                                    </Form.Text>
                                </Form.Group>
                            </Col>

                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label>URL gambar (opsional)</Form.Label>
                                    <Form.Control
                                        type="url"
                                        value={form.imageUrl}
                                        onChange={(event) =>
                                            updateField("imageUrl", event.target.value)
                                        }
                                        placeholder="https://..."
                                    />
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Mulai ditampilkan</Form.Label>
                                    <Form.Control
                                        type="datetime-local"
                                        value={form.startAt}
                                        onChange={(event) =>
                                            updateField("startAt", event.target.value)
                                        }
                                    />
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Akhir ditampilkan</Form.Label>
                                    <Form.Control
                                        type="datetime-local"
                                        value={form.endAt}
                                        onChange={(event) =>
                                            updateField("endAt", event.target.value)
                                        }
                                    />
                                    <Form.Text className="text-muted">
                                        Kosongkan jika tidak ada tanggal berakhir.
                                    </Form.Text>
                                </Form.Group>
                            </Col>

                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>Target *</Form.Label>
                                    <Form.Select
                                        value={form.targetType}
                                        onChange={(event) =>
                                            updateField("targetType", event.target.value)
                                        }
                                    >
                                        <option value="ALL">Semua pengguna</option>
                                        <option value="EMPLOYEE">Petugas/Pegawai</option>
                                        <option value="USER_SERVICE">Pengguna jasa</option>
                                        <option value="ROLE">Role tertentu</option>
                                        <option value="UPT">UPT tertentu</option>
                                        <option value="TRADER">Trader tertentu</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            <Col md={5}>
                                <Form.Group>
                                    <Form.Label>Nilai target</Form.Label>
                                    <Form.Control
                                        value={form.targetValue}
                                        disabled={["ALL", "EMPLOYEE", "USER_SERVICE"].includes(
                                            form.targetType
                                        )}
                                        onChange={(event) =>
                                            updateField("targetValue", event.target.value)
                                        }
                                        placeholder={renderTargetValuePlaceholder()}
                                    />
                                    <Form.Text className="text-muted">
                                        Untuk ROLE/TRADER, beberapa nilai dapat dipisahkan dengan
                                        koma.
                                    </Form.Text>
                                </Form.Group>
                            </Col>

                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label>Prioritas (0–100)</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min={0}
                                        max={100}
                                        value={form.priority}
                                        onChange={(event) =>
                                            updateField("priority", event.target.value)
                                        }
                                    />
                                </Form.Group>
                            </Col>

                            <Col md={12}>
                                <Form.Check
                                    type="switch"
                                    label="Aktifkan pengumuman"
                                    checked={form.active}
                                    onChange={(event) =>
                                        updateField("active", event.target.checked)
                                    }
                                />
                            </Col>
                        </Row>
                    </Modal.Body>

                    <Modal.Footer>
                        <Button
                            variant="secondary"
                            onClick={closeModal}
                            disabled={saving}
                        >
                            Batal
                        </Button>

                        <Button type="submit" disabled={saving}>
                            {saving ? "Menyimpan..." : "Simpan Pengumuman"}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
}
